import { firstSelectableRheology } from 'utils/rheology';
import PCPipe from '../../MineModel/Pipes/PCPipe';
import { calculateFrictionForRoute } from '../Model/updateFriction';
import { updateRecipeState } from '../Model/updateRecipeState';
import { MinVelocityResult, RecipeState, VelocityResult } from '../../MineModel/types';
import { wilsonsNomograph } from './PCOtherFunctions';
import { Model } from '../../hooks/useModelState';
import { liveRouteFor } from '../../MineModel/Route/utils';
import PCRoute from '../../MineModel/Route/PCRoute';

export function binderPercentFromRatio(ratio: number) {
  return (ratio / (1 + ratio));
}

function findMinVelocity(pipes: Record<string, PCPipe>, flowrate: number) {
  const pipeKeys = Object.keys(pipes);
  const initPipeKey = pipeKeys[0];
  let minVelocity = pipes[initPipeKey].getVelocity(flowrate);
  let returnKey = pipeKeys[0];
  pipeKeys.forEach((key) => {
    const pipe = pipes[key as any];
    const velocity = pipe.getVelocity(flowrate);
    if (velocity < minVelocity) {
      returnKey = key;
      minVelocity = velocity;
    }
  });
  return {
    pipe: pipes[returnKey as any],
    velocity: minVelocity,
  };
}

// Function called during optimisation or manual adjustment
export function hydraulicStrengthFrR(
  model: Model,
  binderTailingsRatio: number,
  dryConcentrationByMass: number,
  // Rheology
  newTailingsSolidsDensity?: number,
  newBinderParticleDensity?: number,
  newCarrierFluidDensity?: number,
  // throughput
  newTailingsDryTonnage?: number,
  newWetFlowRate?: number,
  newWetTonnage?: number,
) {
  const recipeState = model.recipeState as RecipeState;
  const selectedRoute = liveRouteFor(model.routes) as PCRoute;
  const binderConcentration = binderPercentFromRatio(binderTailingsRatio);
  let minV: MinVelocityResult | null = null;
  let settlingV: VelocityResult | null = null;
  let maxPress = 0;
  if (selectedRoute) {
    model.setRecipeState(updateRecipeState(
      model,
      recipeState,
      dryConcentrationByMass,
      binderConcentration,
      newTailingsSolidsDensity,
      newBinderParticleDensity,
      newCarrierFluidDensity,
      newTailingsDryTonnage,
      newWetFlowRate,
      newWetTonnage,
    ));
    calculateFrictionForRoute(model);
    const wfr = recipeState?.wetFlowRate ?? 0;
    const tsd = recipeState?.tailingsSolidsDensity ?? 0;
    if (wfr && tsd) {
      minV = findMinVelocity(selectedRoute.pipeList, wfr!);
      selectedRoute.setMinimumVelocity(minV.velocity);
      settlingV = wilsonsNomograph(tsd, minV.pipe.insideDiameter);
      if (settlingV) {
        selectedRoute.setSettlingVelocity(settlingV.velocity);
      }
      maxPress = selectedRoute.findMaxPipePressure() ?? 0;
    }
  }
  return {
    recipeState: model.recipeState,
    hydraulicCalculateResult: {
      minVelocity: minV?.velocity ?? 0,
      settlingVelocity: settlingV?.velocity ?? 0,
      maxPipePressure: maxPress,
    },
  };
}

interface UpdateSteadyStateInputs {
  model: Model,
  binderConcentration: number,
  drySolidsConcentrationByMass: number,
  newTailingsSolidsDensity?: number,
  newBinderParticleDensity?: number,
  newCarrierFluidDensity?: number,
  newTailingsDryTonnage?: number,
  newWetFlowRate?: number,
  newWetTonnage?: number,
}

const updateSteadyState = ({
  model,
  binderConcentration,
  drySolidsConcentrationByMass,
  newTailingsSolidsDensity,
  newBinderParticleDensity,
  newCarrierFluidDensity,
  newTailingsDryTonnage,
  newWetFlowRate,
  newWetTonnage,
}: UpdateSteadyStateInputs) => {
  const selectedRoute = liveRouteFor(model.routes) as PCRoute;
  const recipeState = model.recipeState as RecipeState;
  const pumpPressureKpa = recipeState?.wetDensity ? selectedRoute?.pumpPressureKpa as number : undefined;
  if (selectedRoute) {
    model.setRecipeState(updateRecipeState(
      model,
      recipeState,
      drySolidsConcentrationByMass,
      binderConcentration,
      newTailingsSolidsDensity,
      newBinderParticleDensity,
      newCarrierFluidDensity,
      newTailingsDryTonnage,
      newWetFlowRate,
      newWetTonnage,
      pumpPressureKpa,
    ));
    calculateFrictionForRoute(model);
    selectedRoute.findWarnings();
  }
};

export function hydraulicRecipeFrS(
  model: Model,
  binderTailingsRatio: number,
  // Rheology
  newTailingsSolidsDensity?: number,
  newBinderParticleDensity?: number,
  newCarrierFluidDensity?: number,
  // throughput
  newTailingsDryTonnage?: number,
  newWetFlowRate?: number,
  newWetTonnage?: number,
) {
  const binderConcentration = binderPercentFromRatio(binderTailingsRatio);
  const recipeState = model.recipeState as RecipeState;
  const activeRheology = model.rheology.find((r) => r.rheologyDataSetId === recipeState?.rheologyDataSetId)
    ?? firstSelectableRheology(model.rheology) ?? model.rheology[0];
  const fluid = activeRheology.massConcentration;
  const selectedRoute = liveRouteFor(model.routes) as PCRoute;
  const { min, max, interval } = fluid;
  const drySolidsConcentrationByMass = min;
  let count = 0;

  const steadyStateInputs: UpdateSteadyStateInputs = {
    model,
    binderConcentration,
    drySolidsConcentrationByMass,
    newTailingsSolidsDensity,
    newBinderParticleDensity,
    newCarrierFluidDensity,
    newTailingsDryTonnage,
    newWetFlowRate,
    newWetTonnage,
  };

  if (selectedRoute) {
    for (steadyStateInputs.drySolidsConcentrationByMass = min;
      steadyStateInputs.drySolidsConcentrationByMass < max + interval;
      steadyStateInputs.drySolidsConcentrationByMass += interval
    ) {
      updateSteadyState(steadyStateInputs);
      count += 1;

      const hasOverPressureWarning = selectedRoute.warnings.some((warning) => warning.code === 'OP');

      if (recipeState.pumpPressureKpa! > 10) break;
      if (hasOverPressureWarning) break;
      if (count > 1000) break;
    }

    steadyStateInputs.drySolidsConcentrationByMass -= interval;
    if (steadyStateInputs.drySolidsConcentrationByMass > max) steadyStateInputs.drySolidsConcentrationByMass = max;
    if (steadyStateInputs.drySolidsConcentrationByMass < min) steadyStateInputs.drySolidsConcentrationByMass = min;

    updateSteadyState(steadyStateInputs);
  }
}
